import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthConfig, isAuthenticated } from '../utils/auth';
import '../App.css';
import AppNav from '../components/AppNav';

const CANCEL_REASONS = [
  { value: 'too_expensive',    label: 'Too expensive' },
  { value: 'not_using',        label: "Not using it enough" },
  { value: 'switching',        label: 'Switching to another service' },
  { value: 'missing_features', label: 'Missing features I need' },
  { value: 'technical_issues', label: 'Technical issues' },
  { value: 'temporary_pause',  label: 'Pausing temporarily' },
  { value: 'other',            label: 'Other' },
];

const REASON_FOLLOWUPS = {
  too_expensive: [
    { key: 'budget', type: 'text', label: 'What price would you be happy to pay?', placeholder: 'e.g. $10/month', required: true },
  ],
  switching: [
    { key: 'competitor', type: 'text', label: 'Which service are you switching to?', placeholder: 'e.g. Coolify, Render, Railway...', required: true },
    { key: 'competitor_reason', type: 'textarea', label: "What does it offer that we don't?", placeholder: 'What made you choose it over OpenHosst?', required: false },
  ],
  missing_features: [
    { key: 'features', type: 'textarea', label: 'What features do you need?', placeholder: 'Describe the features that were missing...', required: true },
  ],
  technical_issues: [
    { key: 'issue', type: 'textarea', label: 'What issues did you experience?', placeholder: 'Describe the problems you encountered...', required: true },
  ],
  not_using: [
    { key: 'improvement', type: 'textarea', label: 'What would have kept you using it?', placeholder: 'Any suggestions or missing use cases...', required: true },
  ],
  temporary_pause: [
    { key: 'return_when', type: 'text', label: 'When do you plan to return?', placeholder: 'e.g. In 3 months, After the project ends...', required: true },
  ],
  other: [
    { key: 'details', type: 'textarea', label: 'Please tell us more', placeholder: "Tell us why you're leaving...", required: true },
  ],
};

function CancelSubscriptionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { service } = useParams();
  const { subscription } = location.state || {};

  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [followupAnswers, setFollowupAnswers] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate(`/${service}`);
      return;
    }
    if (!subscription) {
      navigate(`/${service}`);
    }
  }, [navigate, service, subscription]);

  const followups = REASON_FOLLOWUPS[selectedReason] || [];
  const requiredsMet = followups.filter(f => f.required).every(f => (followupAnswers[f.key] || '').trim().length > 0);
  const canSubmit = !!selectedReason && requiredsMet;

  const handleSelectReason = (value) => {
    setSelectedReason(value);
    setFollowupAnswers({});
  };

  const handleCancel = async () => {
    if (!canSubmit) return;
    try {
      setIsCancelling(true);
      setError(null);

      const structured = {};
      followups.forEach(f => {
        const val = (followupAnswers[f.key] || '').trim();
        if (val) structured[f.key] = val;
      });

      const response = await axios.post(
        `${API_URL}/api/subscriptions/cancel`,
        {
          subscriptionId: subscription.id,
          cancellationReason: selectedReason,
          cancellationDetails: Object.keys(structured).length > 0 ? JSON.stringify(structured) : '',
        },
        getAuthConfig()
      );

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/${service}`);
        }, 2500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleKeepSubscription = () => {
    navigate(`/${service}`);
  };

  if (!subscription) return null;

  if (success) {
    return (
      <div className="checkout-container-new">
        <AppNav onLogout={() => {}} />
        <div className="email-entry-container">
          <div className="email-entry-card">
            <div style={{ textAlign: 'center' }}>
              <i className="fa-solid fa-circle-check" style={{ fontSize: '64px', color: '#40E0D0', marginBottom: '24px', filter: 'drop-shadow(0 0 20px rgba(64, 224, 208, 0.4))' }}></i>
              <h1 className="email-entry-title">Cancellation Scheduled</h1>
              <p className="email-entry-subtitle" style={{ marginBottom: '32px' }}>
                Your subscription has been scheduled for cancellation. You'll continue to have access until the end of your billing period.
              </p>
              <div className="loading-container" style={{ marginBottom: '16px' }}>
                <div className="loading-spinner"></div>
              </div>
              <p className="loading-text">Redirecting to plan selection...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const inputStyle = (isRequired, value) => ({
    width: '100%',
    padding: '12px 14px',
    borderRadius: '10px',
    border: `2px solid ${isRequired && !value.trim() ? 'rgba(255,107,107,0.4)' : '#3d3d3d'}`,
    background: '#2d2d2d',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  });

  return (
    <div className="checkout-container-new">
      <AppNav onLogout={() => {}} />
      <div className="email-entry-container">
        <div className="email-entry-card">
          <div className="email-entry-icon" style={{ background: 'rgba(255, 107, 107, 0.1)' }}>
            <i className="fa-solid fa-exclamation-triangle" style={{ color: '#ff6b6b' }}></i>
          </div>
          <h1 className="email-entry-title">Cancel Subscription</h1>
          <p className="email-entry-subtitle">
            Before you go, please let us know why you're cancelling.
          </p>

          {error && (
            <div className="alert alert-error" style={{ marginBottom: '20px' }}>
              {error}
            </div>
          )}

          {/* Subscription details */}
          <div style={{
            background: 'rgba(64, 224, 208, 0.05)',
            border: '1px solid rgba(64, 224, 208, 0.2)',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '24px',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', color: '#cccccc', fontSize: '14px' }}>
              <div><strong style={{ color: '#40E0D0' }}>Plan:</strong> {subscription.planName}</div>
              {subscription.domain && (
                <div><strong style={{ color: '#40E0D0' }}>Domain:</strong> {subscription.domain}</div>
              )}
              <div>
                <strong style={{ color: '#40E0D0' }}>Active until:</strong>{' '}
                <span style={{ color: '#ffb3b3', fontWeight: '600' }}>
                  {new Date(subscription.endDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            </div>
          </div>

          {/* Reason selection */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff', marginBottom: '12px' }}>
              Reason for cancelling <span style={{ color: '#ff6b6b' }}>*</span>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {CANCEL_REASONS.map(r => {
                const isSelected = selectedReason === r.value;
                return (
                  <label
                    key={r.value}
                    onClick={() => handleSelectReason(r.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: `2px solid ${isSelected ? '#ff6b6b' : '#3d3d3d'}`,
                      background: isSelected ? 'rgba(255, 107, 107, 0.08)' : '#2d2d2d',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      userSelect: 'none',
                    }}
                  >
                    <div style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      border: `2px solid ${isSelected ? '#ff6b6b' : '#555'}`,
                      background: isSelected ? '#ff6b6b' : 'transparent',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s ease',
                    }}>
                      {isSelected && (
                        <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#fff' }} />
                      )}
                    </div>
                    <span style={{ fontSize: '14px', color: isSelected ? '#ffffff' : '#cccccc', fontWeight: isSelected ? '600' : '400' }}>
                      {r.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Per-reason follow-up fields */}
          {selectedReason && followups.length > 0 && (
            <div style={{
              marginBottom: '24px',
              padding: '16px',
              background: 'rgba(255,107,107,0.04)',
              border: '1px solid rgba(255,107,107,0.15)',
              borderRadius: '12px',
            }}>
              {followups.map(field => (
                <div key={field.key} style={{ marginBottom: followups.indexOf(field) < followups.length - 1 ? '16px' : 0 }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>
                    {field.label}
                    {field.required && <span style={{ color: '#ff6b6b', marginLeft: '4px' }}>*</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={followupAnswers[field.key] || ''}
                      onChange={e => setFollowupAnswers(prev => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      rows={3}
                      style={{ ...inputStyle(field.required, followupAnswers[field.key] || ''), resize: 'vertical' }}
                    />
                  ) : (
                    <input
                      type="text"
                      value={followupAnswers[field.key] || ''}
                      onChange={e => setFollowupAnswers(prev => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      style={inputStyle(field.required, followupAnswers[field.key] || '')}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              onClick={handleCancel}
              className="btn-continue"
              disabled={isCancelling || !canSubmit}
              style={{
                background: 'transparent',
                color: canSubmit ? '#ff6b6b' : '#666',
                border: `2px solid ${canSubmit ? '#ff6b6b' : '#444'}`,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                opacity: canSubmit ? 1 : 0.6,
              }}
            >
              {isCancelling ? (
                <>
                  <span className="spinner" style={{ borderTopColor: '#ff6b6b' }}></span>
                  Cancelling...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-times-circle"></i>
                  Yes, Cancel Subscription
                </>
              )}
            </button>

            <button
              onClick={handleKeepSubscription}
              className="btn-continue"
              disabled={isCancelling}
            >
              Keep Subscription
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <p className="email-entry-note" style={{ marginTop: '24px' }}>
            You can reactivate your subscription anytime before the cancellation date.
          </p>
        </div>
      </div>
    </div>
  );
}

export default CancelSubscriptionPage;
